const PREFIX = "eternua.publication-draft.v2";
export const PUBLICATION_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function storageAvailable() {
  return typeof window !== "undefined" && Boolean(window.localStorage);
}

function ownerKey(ownerId) {
  return encodeURIComponent(String(ownerId || "anonymous").slice(0, 160));
}

export function publicationDraftKey(ownerId, kind, sourceId = "") {
  return `${PREFIX}.${ownerKey(ownerId)}.${encodeURIComponent(String(kind || "unknown"))}.${encodeURIComponent(String(sourceId || "new"))}`;
}

export function parsePublicationDraft(value) {
  try {
    const parsed = JSON.parse(String(value || ""));
    if (!parsed || parsed.version !== 2 || typeof parsed.form !== "object" || Array.isArray(parsed.form)) return null;
    return {
      version: 2,
      form: parsed.form,
      step: Math.max(1, Math.min(4, Number(parsed.step || 1))),
      savedAt: String(parsed.savedAt || ""),
      serverRevision: parsed.serverRevision == null ? null : Number(parsed.serverRevision),
    };
  } catch (_error) {
    return null;
  }
}

export function readPublicationDraft(ownerId, kind, sourceId = "") {
  if (!storageAvailable()) return null;
  const key = publicationDraftKey(ownerId, kind, sourceId);
  const parsed = parsePublicationDraft(window.localStorage.getItem(key));
  const savedAt = parsed ? Date.parse(parsed.savedAt) : NaN;
  if (!parsed || !Number.isFinite(savedAt) || Date.now() - savedAt > PUBLICATION_DRAFT_MAX_AGE_MS) {
    try { window.localStorage.removeItem(key); } catch (_error) { /* noop */ }
    return null;
  }
  return parsed;
}

export function writePublicationDraft(ownerId, kind, sourceId, { form, step, serverRevision }) {
  if (!storageAvailable()) return null;
  const payload = {
    version: 2,
    form,
    step,
    serverRevision,
    savedAt: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(publicationDraftKey(ownerId, kind, sourceId), JSON.stringify(payload));
    return payload;
  } catch (_error) {
    return null;
  }
}

export function clearPublicationDraft(ownerId, kind, sourceId = "") {
  if (!storageAvailable()) return;
  try {
    window.localStorage.removeItem(publicationDraftKey(ownerId, kind, sourceId));
  } catch (_error) {
    // A refused localStorage operation must never make the server draft fail.
  }
}

export function clearPublicationDraftsForOwner(ownerId) {
  if (!storageAvailable() || !ownerId) return;
  const ownerPrefix = `${PREFIX}.${ownerKey(ownerId)}.`;
  try {
    const keys = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(ownerPrefix)) keys.push(key);
    }
    keys.forEach((key) => window.localStorage.removeItem(key));
  } catch (_error) {
    // Logout must continue when storage is denied by the browser.
  }
}

export function changedDraftFields(first = {}, second = {}) {
  return [...new Set([...Object.keys(first), ...Object.keys(second)])]
    .filter((key) => JSON.stringify(first[key] ?? null) !== JSON.stringify(second[key] ?? null))
    .sort();
}
